import { ATTRIBUTION_METHODS } from "helpers/risk"
import { getGroupByQueryParameters, getGroupBySubqueryParameters, getGroupByFields, sanitizePositionSet } from "../helpers"
import { recursiveParseContributors } from "./helpers/contributor-utils"
import { getRiskTotal } from "./helpers/risk-utils"
import { sumNestedContributors } from "./helpers/marginal-risk-utils"

const MARGINAL_CONTRIBUTION_FIELDS = `
volatility
correlation
marginalContribution
riskContribution`

function mapMarginalContribution(risk) {
  const attributionExposureRiskCorrelation = risk?.attribution?.exposureRiskCorrelation
  const exposureRiskCorrelation = risk?.exposureRiskCorrelation

  if (!attributionExposureRiskCorrelation && !exposureRiskCorrelation) {
    return {}
  }

  return {
    total: exposureRiskCorrelation,
    factors: attributionExposureRiskCorrelation?.factors,
    specific: attributionExposureRiskCorrelation?.specific,
  }
}

function getMarginalContributionSubQuery(groupBy = false) {
  return `
    ${groupBy ? "" : "total"}
    exposureRiskCorrelation {
      ${MARGINAL_CONTRIBUTION_FIELDS}
    }
    attribution {
      exposureRiskCorrelation {
        factors {
          ${MARGINAL_CONTRIBUTION_FIELDS}
        }
        specific {
          ${MARGINAL_CONTRIBUTION_FIELDS}
        }
      }
    }`
}

const MARGINAL_CONTRIBUTORS_SUBQUERY = `
  id
  modelProviderId
  bloombergTicker
  assetClass
  assetSubclass
  description
  country
  currency
  notHeld
  total
  attribution {
    summary {
      factors
      specific
    }
    exposureRiskCorrelation {
      factors {
        ${MARGINAL_CONTRIBUTION_FIELDS}
      }
      specific {
        ${MARGINAL_CONTRIBUTION_FIELDS}
      }
    }
  }
`

function getUngroupedContributorsSubQuery(shouldUseIntradayQuery) {
  return `
    riskContributors${shouldUseIntradayQuery ? "" : "(includeNotHeld: $includeNotHeld attributionMethod: $attributionMethod)"}{
      ${MARGINAL_CONTRIBUTORS_SUBQUERY}
      ${getMarginalContributionSubQuery(false)}
    }
  `
}

function getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) {
  return `
  groupedRiskContributors(${getGroupBySubqueryParameters(groupByFields)}${shouldUseIntradayQuery ? "" : ", includeNotHeld: $includeNotHeld attributionMethod: $attributionMethod"}) {
    id
    name
    ${getMarginalContributionSubQuery(true)}
    contributors{
      ${MARGINAL_CONTRIBUTORS_SUBQUERY}
      ${getMarginalContributionSubQuery(true)}
    }
  }`
}

function getQuery(groupByFields, shouldUseIntradayQuery) {
  const extraParameters = groupByFields ? getGroupByQueryParameters(groupByFields) : ""

  const innerQuery = `
    risk {
      total
      attribution {
        summary {
            factors
            specific
        }
      }
    }
    ${groupByFields ? getGroupedContributorsSubQuery(groupByFields, shouldUseIntradayQuery) : getUngroupedContributorsSubQuery(shouldUseIntradayQuery)}
  `

  if (shouldUseIntradayQuery) {
    return `
      query($modelId: String!, $positionSet: PositionSetInput!, $on: Date!, $batchId: Id${extraParameters}) {
        model(id: $modelId) {
          simulation:intradaySimulation(positionSet: $positionSet, on: $on, batchId: $batchId) {
            ${innerQuery}
          }
        }
      }
    `
  }

  return `
    query($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $date: Date!, $attributionMethod: RiskContributorAttributionMethod!, $includeNotHeld: Boolean!${extraParameters}) {
      model(id: $modelId) {
        simulation(positionSet: $positionSet, base: $base, from: $date, to: $date) {
          ${innerQuery}
        }
      }
    }
  `
}

function getMarginalContributionRisk(risk) {
  if (!risk) {
    return {}
  }

  const attribution = risk?.attribution
  const summary = attribution?.summary

  const total = getRiskTotal(risk)

  return {
    risk: mapMarginalContribution(risk),
    ...(total && { total }),
    ...(summary && { factors: summary.factors, specific: summary.specific }),
  }
}

export function createMarginalContributionQuery({ prefix, modelId, positionSet, base, to, groupByOptions, ...options }) {
  const groupByFields = getGroupByFields(groupByOptions)
  const { groupBy, groupById, tier } = groupByFields
  const attributionMethod = options.attributionMethod || ATTRIBUTION_METHODS.MCAR
  const sanitizedPositionSet = sanitizePositionSet(positionSet)
  const { on, batchId, shouldUseIntradayQuery } = options

  return {
    prefix,
    key: ["risk", "marginal", prefix, modelId, positionSet, base, to, groupBy, groupById, tier, shouldUseIntradayQuery, on, batchId],
    query: getQuery(groupBy && groupByFields, shouldUseIntradayQuery),
    variables: {
      modelId,
      positionSet: sanitizedPositionSet,
      base,
      date: to,
      on,
      batchId,
      ...groupByFields,
      attributionMethod,
      includeNotHeld: !!base,
    },
    select: (result) => {
      const simulation = result.data.model.simulation
      const risk = simulation.risk?.[0]
      const contributors = groupBy
        ? simulation.groupedRiskContributors
        : simulation.riskContributors

      const children = recursiveParseContributors(prefix, contributors, { ...options, groupBy }, getMarginalContributionRisk, sumNestedContributors)
      if (options.getResponseMessage) {
        options.getResponseMessage(result)
      }
      return {
        label: "Total",
        values: {
          [prefix]: getMarginalContributionRisk(risk),
        },
        children,
      }
    },
  }
}
